import { Component, Input } from '@angular/core';
import dayjs from 'dayjs';
import {
  CandidateEmailEngagementEmail,
  CandidateEmailEngagementEvent,
  CandidateEmailEngagementHistory,
  getEmailEngagementStatusClass,
  getEmailEngagementStatusIconClass,
  getEmailEngagementStatusKey
} from 'src/app/core/models/candidate-email-engagement.model';

interface TimelineEvent {
  eventType: string;
  timestamp: string;
  reason?: string | null;
  statusClass: string;
  iconClass: string;
  labelKey: string;
  time: string | null;
}

interface TimelineEmail {
  key: string;
  messageType?: string;
  events: TimelineEvent[];
}

@Component({
  selector: 'email-engagement-history',
  templateUrl: './email-engagement-history.component.html',
  styleUrls: ['./email-engagement-history.component.scss']
})
export class EmailEngagementHistoryComponent {
  @Input() history: CandidateEmailEngagementHistory | null = null;

  get hasEngagement(): boolean {
    return !!this.history?.hasEngagement;
  }

  get statusClass(): string {
    return getEmailEngagementStatusClass(this.history?.lastEventStatus);
  }

  get statusLabelKey(): string {
    return getEmailEngagementStatusKey(this.history?.lastEventStatus);
  }

  get isOpened(): boolean {
    return !!this.history?.isOpened;
  }

  get lastUpdated(): string | null {
    return this.formatTime(this.history?.lastEventTimestamp || this.history?.sentAt);
  }

  get timelineEmails(): TimelineEmail[] {
    return (this.history?.emails ?? []).map((email, index) => ({
      key: email.itemId || `${email.sequenceId || ''}-${email.messageType || index}`,
      messageType: email.messageType,
      events: this.mapEvents(email.events)
    }));
  }

  trackByEmail(_index: number, email: TimelineEmail): string {
    return email.key;
  }

  trackByEvent(index: number, event: TimelineEvent): string {
    return `${event.eventType}-${event.timestamp}-${index}`;
  }

  private mapEvents(events: CandidateEmailEngagementEvent[] = []): TimelineEvent[] {
    const ordered = events.slice().sort((a, b) => {
      const aTime = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
      return aTime - bTime;
    });

    return ordered.map(event => {
      const type = (event.eventType || '').toLowerCase();

      return {
        eventType: event.eventType,
        timestamp: event.timestamp,
        reason: event.reason,
        statusClass: getEmailEngagementStatusClass(event.eventType),
        iconClass: this.getEventIconClass(type),
        labelKey: getEmailEngagementStatusKey(event.eventType),
        time: this.formatTime(event.timestamp)
      };
    });
  }

  private getEventIconClass(type: string): string {
    return getEmailEngagementStatusIconClass(type);
  }

  private formatTime(value?: string | null): string | null {
    if (!value) {
      return null;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('MM/DD/YYYY [at] h:mm A') : null;
  }
}
